/** Custom Report — period-filtered editable trade list for Admin / Super User. */

export const CUSTOM_REPORT_PROPERTY_TYPES = [
  { value: "Residential", label: "Residential" },
  { value: "Commercial", label: "Commercial" },
];

export const CUSTOM_REPORT_DEAL_TYPES = [
  { value: "Lease", label: "Lease" },
  { value: "Sale", label: "Sale" },
];

export const CUSTOM_REPORT_STATUSES = [
  { value: "Open", label: "Open" },
  { value: "Closed", label: "Closed" },
  { value: "Terminated", label: "Terminated" },
  { value: "Fall", label: "Fall" },
];

export const CUSTOM_REPORT_CLASSIFICATIONS = [
  { value: "Listing Side", label: "Listing Side" },
  { value: "Co-operating Side", label: "Co-operating Side" },
];

export const CUSTOM_REPORT_COLUMNS = [
  { key: "tradeNumber", label: "Trade #", edit: "select" },
  { key: "clientNames", label: "Client Names", edit: "select" },
  { key: "propertyType", label: "Property Type", edit: "select" },
  { key: "dealType", label: "Deal Type", edit: "select" },
  { key: "status", label: "Status", edit: "select" },
  { key: "closingDate", label: "Closing Date", edit: "date" },
  { key: "classification", label: "Classification", edit: "select" },
  { key: "agentName", label: "Agent Name", edit: "select" },
  { key: "streetNumber", label: "Street #", edit: "text" },
  { key: "unit", label: "Unit", edit: "text" },
  { key: "streetName", label: "Street Name", edit: "text" },
  { key: "city", label: "City", edit: "text" },
  { key: "province", label: "Province", edit: "text" },
  { key: "postalCode", label: "Postal Code", edit: "text" },
];

/** No column dropdowns — use secondary filters instead. */
export const CUSTOM_REPORT_SELECT_KEYS = new Set();

/** Six secondary filter controls (above the table). */
export const CUSTOM_REPORT_SECONDARY_FILTERS = [
  { key: "propertyType", label: "Property Type" },
  { key: "dealType", label: "Deal Type" },
  { key: "status", label: "Status" },
  { key: "classification", label: "Classification" },
  { key: "agentName", label: "Agent Name" },
  { key: "clientNames", label: "Client Names" },
];

export function emptyCustomReportSecondaryFilters() {
  return CUSTOM_REPORT_SECONDARY_FILTERS.reduce((acc, f) => {
    acc[f.key] = "";
    return acc;
  }, {});
}

export function getCustomReportSecondaryFilterOptions(key, rows, agentOptions) {
  if (key === "propertyType") {
    return [
      { value: "", label: "Any property type" },
      ...CUSTOM_REPORT_PROPERTY_TYPES,
    ];
  }
  if (key === "dealType") {
    return [
      { value: "", label: "Any deal type" },
      ...CUSTOM_REPORT_DEAL_TYPES,
    ];
  }
  if (key === "status") {
    return [{ value: "", label: "Any status" }, ...CUSTOM_REPORT_STATUSES];
  }
  if (key === "classification") {
    return [
      { value: "", label: "Any classification" },
      ...CUSTOM_REPORT_CLASSIFICATIONS,
    ];
  }
  if (key === "agentName") {
    const opts = agentOptions || [];
    return [{ value: "", label: "Any agent" }, ...opts];
  }
  if (key === "clientNames") {
    const seen = new Set();
    const opts = [];
    for (const row of rows || []) {
      const v = clean(row.clientNames);
      if (!v || seen.has(v)) continue;
      seen.add(v);
      opts.push({ value: v, label: v });
    }
    opts.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
    );
    return [{ value: "", label: "Any client" }, ...opts];
  }
  return [{ value: "", label: "Any" }];
}

export function applyCustomReportSecondaryFilters(rows, filters) {
  const active = Object.entries(filters || {}).filter(
    ([, v]) => v != null && String(v).trim() !== "",
  );
  if (!active.length) return rows || [];
  return (rows || []).filter((row) =>
    active.every(([key, selected]) => {
      const cell = clean(row[key]);
      return cell === String(selected).trim();
    }),
  );
}

/** Address fields are free text; everything else uses a dropdown (or date). */
export const CUSTOM_REPORT_ADDRESS_KEYS = new Set([
  "streetNumber",
  "unit",
  "streetName",
  "city",
  "province",
  "postalCode",
]);

function clean(value) {
  return String(value ?? "").trim();
}

function personFullName(p) {
  if (!p) return "";
  const name = [p.firstName, p.lastName].filter(Boolean).join(" ").trim();
  return name || clean(p.name) || clean(p.companyName);
}

export function formatIsoToMMDDYYYY(iso) {
  if (!iso) return "";
  const raw = String(iso).trim();
  const mdy = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mdy) {
    return `${mdy[1].padStart(2, "0")}/${mdy[2].padStart(2, "0")}/${mdy[3]}`;
  }
  const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) return `${ymd[2]}/${ymd[3]}/${ymd[1]}`;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${mm}/${dd}/${d.getFullYear()}`;
}

export function formatMMDDYYYYToIso(mdy) {
  if (!mdy) return "";
  const raw = String(mdy).trim();
  const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) return `${ymd[1]}-${ymd[2]}-${ymd[3]}`;
  const m = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return "";
  return `${m[3]}-${m[1].padStart(2, "0")}-${m[2].padStart(2, "0")}`;
}

export function closingDateIsoForInput(value) {
  return formatMMDDYYYYToIso(value) || "";
}

function parseIsoDateLocal(iso) {
  if (!iso) return null;
  const ymd = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) {
    return new Date(
      Number(ymd[1]),
      Number(ymd[2]) - 1,
      Number(ymd[3]),
      12,
      0,
      0,
    );
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function normalizeCustomReportStatus(value) {
  const raw = clean(value);
  if (!raw) return "Open";
  const upper = raw.toUpperCase();
  if (/closed|sold|leased|final/i.test(upper)) return "Closed";
  if (/term/i.test(upper)) return "Terminated";
  if (/fall|fallen|thru|through|cancel/i.test(upper)) return "Fall";
  if (/open|avail|pending|firm/i.test(upper)) return "Open";
  const match = CUSTOM_REPORT_STATUSES.find(
    (o) => o.value.toUpperCase() === upper || o.label.toUpperCase() === upper,
  );
  return match?.value || "Open";
}

export function normalizeCustomReportClassification(value) {
  const raw = clean(value);
  if (!raw) return "";
  if (/co-?op|selling/i.test(raw)) return "Co-operating Side";
  if (/list/i.test(raw)) return "Listing Side";
  if (/double/i.test(raw)) return "Listing Side";
  return (
    CUSTOM_REPORT_CLASSIFICATIONS.find(
      (o) => o.value.toLowerCase() === raw.toLowerCase(),
    )?.value || raw
  );
}

export function normalizeCustomReportPropertyType(value) {
  const raw = clean(value);
  if (/comm/i.test(raw)) return "Commercial";
  if (/res|condo|house|town/i.test(raw)) return "Residential";
  return raw || "";
}

export function normalizeCustomReportDealType(value) {
  const raw = clean(value);
  if (/lease/i.test(raw)) return "Lease";
  if (/sale|purchase/i.test(raw)) return "Sale";
  return raw || "";
}

function getTradeClosingIso(trade) {
  const k = trade?.keyInfo || {};
  return clean(k.closeDate || k.closingDate || trade?.closeDate || "");
}

function getClientNames(trade) {
  const people = Array.isArray(trade?.people) ? trade.people : [];
  const sellers = people
    .filter((p) => /seller|landlord|vendor/i.test(p?.type || ""))
    .map(personFullName)
    .filter(Boolean);
  const buyers = people
    .filter((p) => /buyer|tenant|purchaser/i.test(p?.type || ""))
    .map(personFullName)
    .filter(Boolean);
  const combined = [...sellers, ...buyers];
  if (combined.length) return combined.join(", ");
  const k = trade?.keyInfo || {};
  if (k.sellerFirstName || k.sellerLastName) {
    return [k.sellerFirstName, k.sellerLastName].filter(Boolean).join(" ");
  }
  return people.map(personFullName).filter(Boolean).join(", ");
}

function getClientNameOptions(trade) {
  const people = Array.isArray(trade?.people) ? trade.people : [];
  const names = people.map(personFullName).filter(Boolean);
  const sellers = people
    .filter((p) => /seller|landlord|vendor/i.test(p?.type || ""))
    .map(personFullName)
    .filter(Boolean);
  const buyers = people
    .filter((p) => /buyer|tenant|purchaser/i.test(p?.type || ""))
    .map(personFullName)
    .filter(Boolean);
  const options = new Set();
  if (sellers.length) options.add(sellers.join(", "));
  if (buyers.length) options.add(buyers.join(", "));
  if (sellers.length && buyers.length) {
    options.add([...sellers, ...buyers].join(", "));
  }
  names.forEach((n) => options.add(n));
  const current = getClientNames(trade);
  if (current) options.add(current);
  return [...options].map((value) => ({ value, label: value }));
}

function getPrimaryAgentName(trade) {
  const list = trade?.agentCommissionList || [];
  if (!list.length) return "";
  const lead =
    list.find((a) => /lead|listing|yes/i.test(String(a.agentLead || ""))) ||
    list[0];
  return clean(lead?.agentName || "");
}

export function buildCustomReportRow(trade) {
  const k = trade?.keyInfo || {};
  const closingIso = getTradeClosingIso(trade);
  return {
    rowId: String(trade?._id || trade?.tradeNumber || Math.random()),
    tradeId: trade?._id || "",
    tradeNumber: clean(trade?.tradeNumber || k.tradeNumber),
    clientNames: getClientNames(trade),
    clientNameOptions: getClientNameOptions(trade),
    propertyType: normalizeCustomReportPropertyType(k.propertyType || k.type),
    dealType: normalizeCustomReportDealType(k.dealType || k.type),
    status: normalizeCustomReportStatus(k.status),
    closingDate: formatIsoToMMDDYYYY(closingIso),
    closingDateIso: closingIso,
    classification: normalizeCustomReportClassification(k.classification),
    agentName: getPrimaryAgentName(trade),
    streetNumber: clean(k.streetNumber),
    unit: clean(k.unit),
    streetName: clean(k.streetName),
    city: clean(k.city),
    province: clean(k.province),
    postalCode: clean(k.postalCode),
    dirty: false,
  };
}

export function tradeInClosingPeriod(trade, fromIso, toIso) {
  const closing = getTradeClosingIso(trade);
  if (!closing) return false;
  const d = parseIsoDateLocal(closing);
  if (!d) return false;
  if (fromIso) {
    const from = parseIsoDateLocal(fromIso);
    if (from && d < from) return false;
  }
  if (toIso) {
    const to = parseIsoDateLocal(toIso);
    if (to && d > to) return false;
  }
  return true;
}

export function buildCustomReportRows(trades, fromIso, toIso) {
  return (trades || [])
    .filter((t) => tradeInClosingPeriod(t, fromIso, toIso))
    .map(buildCustomReportRow)
    .sort((a, b) => {
      const na = parseInt(String(a.tradeNumber).match(/\d+/)?.[0] || "0", 10);
      const nb = parseInt(String(b.tradeNumber).match(/\d+/)?.[0] || "0", 10);
      return na - nb;
    });
}

export function formatCustomReportPeriodLabel(fromIso, toIso) {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const fmt = (iso) => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-").map(Number);
    if (!y || !m) return iso;
    return `${months[m - 1]} ${String(d).padStart(2, "0")}, ${y}`;
  };
  if (fromIso && toIso) return `${fmt(fromIso)} to ${fmt(toIso)}`;
  if (fromIso) return `${fmt(fromIso)} to Date`;
  if (toIso) return `Through ${fmt(toIso)}`;
  return "";
}

export function propertyAddressDisplay(row) {
  const street = [row.streetNumber, row.streetName].filter(Boolean).join(" ");
  const unit = row.unit ? (street ? `${row.unit}-${street}` : row.unit) : street;
  return unit;
}

export function buildCustomReportTradeOptions(rows) {
  const seen = new Set();
  return (rows || [])
    .map((r) => clean(r.tradeNumber))
    .filter((tn) => {
      if (!tn || seen.has(tn)) return false;
      seen.add(tn);
      return true;
    })
    .map((value) => ({ value, label: value }));
}

export function buildCustomReportAgentOptions(agents, rows) {
  const opts = new Map();
  (agents || []).forEach((a) => {
    const name = clean(
      a.name ||
        [a.firstName, a.lastName].filter(Boolean).join(" ") ||
        a.agentName,
    );
    if (name) opts.set(name, { value: name, label: name });
  });
  (rows || []).forEach((r) => {
    if (r.agentName) opts.set(r.agentName, { value: r.agentName, label: r.agentName });
  });
  return [...opts.values()].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
  );
}

export function getSelectOptionsForColumn(key, row, tradeOptions, agentOptions) {
  if (key === "tradeNumber") return tradeOptions || [];
  if (key === "clientNames") {
    const opts = [...(row.clientNameOptions || [])];
    if (row.clientNames && !opts.some((o) => o.value === row.clientNames)) {
      opts.unshift({ value: row.clientNames, label: row.clientNames });
    }
    return opts;
  }
  if (key === "propertyType") return CUSTOM_REPORT_PROPERTY_TYPES;
  if (key === "dealType") return CUSTOM_REPORT_DEAL_TYPES;
  if (key === "status") return CUSTOM_REPORT_STATUSES;
  if (key === "classification") return CUSTOM_REPORT_CLASSIFICATIONS;
  if (key === "agentName") return agentOptions || [];
  return [];
}

/** Build keyInfo patch for saving overrides back to the trade. */
export function customReportRowToTradePatch(row) {
  const closeIso =
    formatMMDDYYYYToIso(row.closingDate) || row.closingDateIso || "";
  return {
    keyInfo: {
      propertyType: row.propertyType || "",
      dealType: row.dealType || "",
      status: row.status || "",
      closeDate: closeIso,
      classification: row.classification || "",
      streetNumber: row.streetNumber || "",
      unit: row.unit || "",
      streetName: row.streetName || "",
      city: row.city || "",
      province: row.province || "",
      postalCode: row.postalCode || "",
    },
  };
}
